// eslint-disable-next-line import/no-extraneous-dependencies
import {
  AWS_STAGE_TYPE,
  AwsEnvironmentType,
  AwsStageType,
  DEPLOYMENT_TARGET_ROLE,
  DeploymentTargetRoleType,
} from "@codedrifters/utils";
import { Component } from "projen";
import { AwsCdkTypeScriptApp } from "projen/lib/awscdk";
import { AwsDeploymentConfig } from "./aws-deployment-config";
import { CdkTargetOverrides } from "./cdk-cli-options";
import {
  renderCdkBootstrap,
  renderCdkDeploy,
  renderCdkDestroy,
  renderCdkWatch,
} from "./cdk-cli-renderer";
import { GitBranch } from "../git";

/*******************************************************************************
 *
 * AWS Deployment Configuration
 *
 * A single deployment target that CDK applications can be deployed into.
 *
 ******************************************************************************/

/**
 * Represents the configuration for local deployment in AWS.
 */
export interface AwsLocalDeploymentConfig {
  /**
   * The AWS profile (in ~/.aws/config) to use for local deployment.
   *
   * @default generated dynamically based role name, account and region
   */
  readonly profile?: string;

  /**
   * Named Role used to conduct local deployments.
   *
   * @default "poweruseraccess"
   */
  readonly roleName?: string;

  /**
   * The pattern used to identify stacks to deploy in CI deployments.
   *
   * @default `*-${account}-${region}`
   */
  readonly stackPattern?: string;
}

export interface CiDeploymentConfig {
  /**
   * The OIDC IAM Role to assume for CI deployments.
   */
  readonly roleArn: string;

  /**
   * The pattern used to identify stacks to deploy in CI deployments.
   *
   * @default `*-${account}-${region}`
   */
  readonly stackPattern?: string;
}

/**
 * Represents a deployment target in AWS, including account and region, and
 * branches allowed to deploy to this target.
 */
export interface AwsDeploymentTargetOptions {
  /**
   * The account name for the deployment target.
   */
  readonly account: string;

  /**
   * The AWS region for the deployment target.
   */
  readonly region: string;

  /**
   * AWS deployment type, such as dev, stage, or prod.
   *
   * @default 'dev'
   */
  readonly awsStageType?: AwsStageType;

  /**
   * Deployment target role: whether this (account, region) is the primary or
   * secondary deployment target (e.g. primary vs replica region).
   *
   * @default 'primary'
   */
  readonly deploymentTargetRole?: DeploymentTargetRoleType;

  /**
   * AWS environment type, such as primary or secondary.
   *
   * @deprecated Use `deploymentTargetRole` instead. This property is maintained for backward compatibility.
   * @default 'primary'
   */
  readonly awsEnvironmentType?: AwsEnvironmentType;

  /**
   * The AWS profile to use for this deployment target.
   *
   * @default ['main'] when prod release type, ['feature/*'] when dev type
   */
  readonly branches?: Array<GitBranch>;

  /**
   * Can this deployment target be used for local development?
   *
   * @default true for dev environments, false for prod environments
   */
  readonly localDeployment?: boolean;

  /**
   * Configuration when deploying to this target locally.
   */
  readonly localDeploymentConfig?: AwsLocalDeploymentConfig;

  /**
   * Can this deployment target be used in CI deployments?
   *
   * @default false
   */
  readonly ciDeployment?: boolean;

  /*
   * Configuration when deploying to this target in CI.

   */
  readonly ciDeploymentConfig?: CiDeploymentConfig;

  /**
   * Per-target overrides for `cdk` CLI options.
   *
   * Layered on top of the per-stage defaults and any per-account overrides
   * configured on {@link CdkCli}. See {@link CdkCli} for the full precedence
   * chain.
   */
  readonly cdkOptions?: CdkTargetOverrides;
}

export class AwsDeploymentTarget extends Component {
  /**
   * Static method to discovert targets in a project.
   */
  public static of(
    project: AwsCdkTypeScriptApp,
  ): Array<AwsDeploymentTarget> | undefined {
    const isDefined = (c: Component): c is AwsDeploymentTarget =>
      c instanceof AwsDeploymentTarget;
    return project.components.filter(isDefined);
  }

  /**
   * The account name for the deployment target.
   */
  public account: string;

  /**
   * The AWS region for the deployment target.
   */
  public region: string;

  /**'
   * AWS stage type, such as dev, stage, or prod.
   *
   * @default 'dev'
   */
  public awsStageType: AwsStageType;

  /**
   * Deployment target role: whether this (account, region) is the primary or
   * secondary deployment target (e.g. primary vs replica region).
   *
   * @default 'primary'
   */
  public deploymentTargetRole: DeploymentTargetRoleType;

  /**
   * AWS environment type, such as primary or secondary.
   *
   * @deprecated Use `deploymentTargetRole` instead. This property is maintained for backward compatibility.
   * @default 'primary'
   */
  public awsEnvironmentType: AwsEnvironmentType;

  /**
   * The AWS profile to use for this deployment target.
   *
   * @default ['main'] when prod release type, ['feature/*'] when dev type
   */
  public branches: Array<GitBranch> = [];

  /**
   * Can this deployment target be used for local development?
   *
   * @default true for dev environments, false for prod environments
   */
  public localDeployment?: boolean;

  /**
   * Configuration when deploying to this target locally.
   */
  public localDeploymentConfig?: AwsLocalDeploymentConfig;

  /**
   * Can this deployment target be used in CI deployments?
   *
   * @default false
   */
  public ciDeployment?: boolean;

  /*
   * Configuration when deploying to this target in CI.
   */
  public ciDeploymentConfig?: Required<CiDeploymentConfig>;

  /**
   * Configuration for the CDK output directory for this deployment target.
   */
  public awsDeploymentConfig: AwsDeploymentConfig;

  /**
   * Per-target overrides for `cdk` CLI options.
   *
   * Read by `CdkCli.*OptionsFor(target)` as the second-highest layer in the
   * precedence chain (below call-site explicit options, above per-account
   * overrides). See {@link CdkCli} for the full chain.
   */
  public cdkOptions?: CdkTargetOverrides;

  constructor(
    project: AwsCdkTypeScriptApp,
    options: AwsDeploymentTargetOptions,
  ) {
    super(project);

    /**
     * Set target region and account.
     */
    this.account = options.account;
    this.region = options.region;

    /**
     * Per-target CDK CLI overrides (consumed by CdkCli.*OptionsFor).
     */
    this.cdkOptions = options.cdkOptions;

    /**
     * Set default type
     */
    this.awsStageType = options.awsStageType || AWS_STAGE_TYPE.DEV;

    /**
     * Set deployment target role (preferred) or fall back to deprecated awsEnvironmentType.
     */
    const role =
      options.deploymentTargetRole ??
      options.awsEnvironmentType ??
      DEPLOYMENT_TARGET_ROLE.PRIMARY;
    this.deploymentTargetRole = role;
    this.awsEnvironmentType = role;

    /**
     * Set default Branches
     */

    this.branches =
      options.branches ??
      (this.awsStageType === AWS_STAGE_TYPE.PROD
        ? [
            {
              branch: "main",
            },
          ]
        : [
            {
              branch: "feature/*",
            },
          ]);

    /**
     * Set default for local deployment
     */
    this.localDeployment =
      options.localDeployment ?? this.awsStageType === AWS_STAGE_TYPE.DEV;

    /**
     * Some default configurations for local deployments.
     */
    if (this.localDeployment) {
      const roleName =
        options.localDeploymentConfig?.roleName?.toLowerCase() ||
        "poweruseraccess";
      const profile =
        options.localDeploymentConfig?.profile ||
        `${roleName}-${this.awsStageType}-${this.account}-${this.region}`;

      const stackPattern =
        options.localDeploymentConfig?.stackPattern ||
        `${this.awsStageType}/${this.awsEnvironmentType}/*-${this.account}-${this.region}`;

      this.localDeploymentConfig = {
        profile,
        roleName,
        stackPattern,
        ...options.localDeploymentConfig,
      };
    }

    /**
     * Set CI deployment default
     */
    this.ciDeployment = options.ciDeployment ?? false;

    /**
     * Some defaults for CI deployments.
     */
    if (this.ciDeployment) {
      const roleArn =
        options.ciDeploymentConfig?.roleArn ||
        `arn:aws:iam::${this.account}:role/GitHubDeployer}`;

      const stackPattern =
        options.ciDeploymentConfig?.stackPattern ||
        `${this.awsStageType}/${this.awsEnvironmentType}/*-${this.account}-${this.region}`;

      this.ciDeploymentConfig = {
        roleArn,
        stackPattern,
        ...options.ciDeploymentConfig,
      };
    }

    /**
     * Find or create CDK folder config for this project.
     */
    this.awsDeploymentConfig =
      AwsDeploymentConfig.of(project) || new AwsDeploymentConfig(project);

    /**
     * Add the target to the deployment targets array.
     */
    this.awsDeploymentConfig.awsDeploymentTargets.push(this);

    // Deploy tasks are configured per target.
    this.configureDeployTask();

    // Watch tasks are configured per target.
    this.configureWatchTask();

    // Destroy tasks are configured per target.
    this.configureDestroyTask();

    // Bootstrap tasks are configured per target.
    this.configureBootstrapTask();
  }

  /*****************************************************************************
   *
   * Deploy Tasks
   *
   * - If local deploy, add a deploy task.
   *
   ****************************************************************************/

  private configureDeployTask = (): void => {
    if (this.localDeployment) {
      const taskName = [
        "deploy",
        this.awsStageType,
        this.account,
        this.region,
      ].join(":");
      const deployTask = this.project.tasks.addTask(taskName, {
        env: this.awsDeploymentConfig.env,
      });
      const deployOpts = this.awsDeploymentConfig.cdkCli.deployOptionsFor(this);
      deployTask.exec(
        `cdk ${renderCdkDeploy({
          ...deployOpts,
          lookups: false,
          profile: this.localDeploymentConfig?.profile,
          app: this.awsDeploymentConfig.cdkOut,
          stackPatterns: this.localDeploymentConfig?.stackPattern
            ? [this.localDeploymentConfig.stackPattern]
            : undefined,
        })}`,
      );
    }
  };

  /*****************************************************************************
   *
   * Watch tasks
   *
   * - Configure watch task to use the branch name
   * - configure watch task to use the correct synth output location.
   *
   ****************************************************************************/

  private configureWatchTask = (): void => {
    if (this.localDeployment) {
      const taskName = [
        "watch",
        this.awsStageType,
        this.account,
        this.region,
      ].join(":");
      const watchTask = this.project.tasks.addTask(taskName, {
        env: this.awsDeploymentConfig.env,
      });

      // update the synth first
      const synthSilent = this.project.tasks.tryFind("synth:silent");
      if (synthSilent) {
        watchTask.spawn(synthSilent);
      }

      // do a normal deploy
      const deployOpts = this.awsDeploymentConfig.cdkCli.deployOptionsFor(this);
      watchTask.exec(
        `cdk ${renderCdkDeploy({
          ...deployOpts,
          lookups: false,
          profile: this.localDeploymentConfig?.profile,
          app: this.awsDeploymentConfig.cdkOut,
          stackPatterns: this.localDeploymentConfig?.stackPattern
            ? [this.localDeploymentConfig.stackPattern]
            : undefined,
        })}`,
      );

      // watch for changes and log output
      const watchOpts = this.awsDeploymentConfig.cdkCli.watchOptionsFor(this);
      watchTask.exec(
        `cdk ${renderCdkWatch({
          ...watchOpts,
          lookups: false,
          hotswap: true,
          profile: this.localDeploymentConfig?.profile,
          stackPatterns: this.localDeploymentConfig?.stackPattern
            ? [this.localDeploymentConfig.stackPattern]
            : undefined,
        })}`,
      );
    }
  };

  /*****************************************************************************
   *
   * Destroy Tasks
   *
   * - If local deploy, add a destroy task.
   *
   * Mirrors the deploy task family so `destroyDefaults`, per-account
   * `destroy` overrides, and a target's `cdkOptions.destroy` all reach a
   * rendered command. Projen's generic `destroy` passthrough is deliberately
   * left intact as an ad-hoc escape hatch — unlike the generic `deploy` and
   * `watch` tasks, it is not reset to a disabled stub.
   *
   ****************************************************************************/

  private configureDestroyTask = (): void => {
    if (this.localDeployment) {
      const taskName = [
        "destroy",
        this.awsStageType,
        this.account,
        this.region,
      ].join(":");
      const destroyTask = this.project.tasks.addTask(taskName, {
        env: this.awsDeploymentConfig.env,
      });
      const destroyOpts =
        this.awsDeploymentConfig.cdkCli.destroyOptionsFor(this);
      destroyTask.exec(
        `cdk ${renderCdkDestroy({
          ...destroyOpts,
          lookups: false,
          profile: this.localDeploymentConfig?.profile,
          app: this.awsDeploymentConfig.cdkOut,
          stackPatterns: this.localDeploymentConfig?.stackPattern
            ? [this.localDeploymentConfig.stackPattern]
            : undefined,
        })}`,
      );
    }
  };

  /*****************************************************************************
   *
   * Bootstrap Tasks
   *
   * Unlike deploy / watch / destroy, this family is generated for **every**
   * target rather than only those with `localDeployment` enabled. Bootstrapping
   * provisions the CDK toolkit stack for one account/region pair, and a
   * CI-only target needs that done before its first deployment just as much as
   * a local one — gating on `localDeployment` would also make the built-in
   * `prod` default (`--termination-protection`) unreachable, since prod targets
   * default to `localDeployment: false`.
   *
   * The tasks are never invoked automatically; running one is always a
   * deliberate, elevated-credentials operation.
   *
   ****************************************************************************/

  private configureBootstrapTask = (): void => {
    const taskName = [
      "bootstrap",
      this.awsStageType,
      this.account,
      this.region,
    ].join(":");
    const bootstrapTask = this.project.tasks.addTask(taskName, {
      env: this.awsDeploymentConfig.env,
    });
    const bootstrapOpts =
      this.awsDeploymentConfig.cdkCli.bootstrapOptionsFor(this);

    /**
     * `--app` and `--lookups` are deliberately not pinned: bootstrap
     * provisions the toolkit stack and does not read the cloud assembly.
     * `--profile` is pinned only when the target has a local profile to use;
     * a CI-only target falls back to the ambient credentials of whoever runs
     * the task. The environment is passed as a trailing positional.
     */
    bootstrapTask.exec(
      `cdk ${renderCdkBootstrap({
        ...bootstrapOpts,
        profile: this.localDeploymentConfig?.profile,
        stackPatterns: [`aws://${this.account}/${this.region}`],
      })}`,
    );
  };
}
